/**
 * Crypto Module Test Suite
 * Tests all crypto operations with fallback detection
 *
 * Usage (in browser console):
 * import { runCryptoTests } from "./utils/crypto/test-crypto.js"
 * runCryptoTests()
 */

import * as crypto from "./index.js";

/**
 * Test 1: KDF Detection (Argon2id vs PBKDF2)
 */
async function testKdfDetection() {
  console.log("\n=== Test 1: KDF Detection ===");
  try {
    const kdfType = await crypto.getKdfImplementation();
    console.log(`✅ KDF Implementation: ${kdfType.toUpperCase()}`);
    return true;
  } catch (error) {
    console.error("❌ KDF Detection failed:", error);
    return false;
  }
}

/**
 * Test 2: RSA Keypair Generation
 */
async function testRsaKeypairGeneration() {
  console.log("\n=== Test 2: RSA Keypair Generation ===");
  try {
    const keypair = await crypto.generateRSAKeypair();

    console.log("✅ Public Key (first 100 chars):", keypair.publicKey.substring(0, 100));
    console.log("✅ Private Key (first 100 chars):", keypair.privateKey.substring(0, 100));

    // Validate PEM format
    if (
      !keypair.publicKey.includes("BEGIN PUBLIC KEY") ||
      !keypair.privateKey.includes("BEGIN PRIVATE KEY")
    ) {
      throw new Error("Invalid PEM format");
    }

    console.log("✅ PEM format validation passed");
    return keypair;
  } catch (error) {
    console.error("❌ RSA Keypair generation failed:", error);
    return null;
  }
}

/**
 * Test 3: Private Key Encryption/Decryption
 */
async function testPrivateKeyEncryption(keypair) {
  console.log("\n=== Test 3: Private Key Encryption/Decryption ===");
  try {
    const testPassword = "MySecurePassword123!";

    // Encrypt
    console.log("Encrypting private key with password...");
    const { encryptedPrivateKey, envelope } =
      await crypto.encryptPrivateKeyWithPassword(keypair.privateKey, testPassword);

    console.log("✅ Encrypted Private Key (first 50 chars):", encryptedPrivateKey.substring(0, 50));
    console.log("✅ Envelope:", JSON.stringify(envelope, null, 2));

    // Validate envelope
    if (!crypto.isValidPrivateKeyEnvelope(envelope)) {
      throw new Error("Invalid PrivateKeyEnvelope structure");
    }
    console.log("✅ Envelope validation passed");

    // Decrypt
    console.log("Decrypting private key with password...");
    const decryptedPrivateKey = await crypto.decryptPrivateKeyWithPassword(
      encryptedPrivateKey,
      envelope,
      testPassword
    );

    // Verify decryption
    if (decryptedPrivateKey === keypair.privateKey) {
      console.log("✅ Private key decryption successful (matches original)");
    } else {
      throw new Error("Decrypted private key does not match original");
    }

    // Test wrong password
    console.log("Testing wrong password...");
    try {
      await crypto.decryptPrivateKeyWithPassword(
        encryptedPrivateKey,
        envelope,
        "WrongPassword"
      );
      throw new Error("Should have failed with wrong password");
    } catch (error) {
      if (error.message.includes("Incorrect password")) {
        console.log("✅ Wrong password correctly rejected");
      } else {
        throw error;
      }
    }

    return { encryptedPrivateKey, envelope };
  } catch (error) {
    console.error("❌ Private key encryption/decryption failed:", error);
    return null;
  }
}

/**
 * Test 4: MasterKey Generation and Encryption
 */
async function testMasterKeyEncryption(keypair) {
  console.log("\n=== Test 4: MasterKey Encryption/Decryption ===");
  try {
    // Generate MasterKey
    const masterKey = crypto.generateMasterKey();
    console.log("✅ MasterKey generated (32 bytes):", masterKey.length);

    // Encrypt MasterKey with public key
    console.log("Encrypting MasterKey with public key...");
    const encryptedMasterKey = await crypto.encryptMasterKeyWithPublicKey(
      masterKey,
      keypair.publicKey
    );
    console.log("✅ Encrypted MasterKey (first 50 chars):", encryptedMasterKey.substring(0, 50));

    // Create envelope
    const envelope = crypto.createMasterKeyEnvelope(1, 1);
    console.log("✅ MasterKeyEnvelope:", JSON.stringify(envelope, null, 2));

    // Validate envelope
    if (!crypto.isValidMasterKeyEnvelope(envelope)) {
      throw new Error("Invalid MasterKeyEnvelope structure");
    }
    console.log("✅ Envelope validation passed");

    // Decrypt MasterKey with private key
    console.log("Decrypting MasterKey with private key...");
    const decryptedMasterKey = await crypto.decryptMasterKeyWithPrivateKey(
      encryptedMasterKey,
      keypair.privateKey
    );

    // Verify decryption
    if (
      decryptedMasterKey.length === masterKey.length &&
      decryptedMasterKey.every((byte, i) => byte === masterKey[i])
    ) {
      console.log("✅ MasterKey decryption successful (matches original)");
    } else {
      throw new Error("Decrypted MasterKey does not match original");
    }

    return { masterKey, encryptedMasterKey, envelope };
  } catch (error) {
    console.error("❌ MasterKey encryption/decryption failed:", error);
    return null;
  }
}

/**
 * Test 5: Recipient Password Encryption
 */
async function testRecipientPasswordEncryption(masterKeyData) {
  console.log("\n=== Test 5: Recipient Password Encryption/Decryption ===");
  try {
    const testEmail = "test@example.com";
    const testPassword = "SecretRecipientPassword123!";

    // Encrypt recipient password
    console.log("Encrypting recipient email + password...");
    const encrypted = await crypto.encryptRecipientPassword(
      testEmail,
      testPassword,
      masterKeyData.masterKey
    );

    console.log("✅ Encrypted Email (first 50 chars):", encrypted.encryptedEmail.substring(0, 50));
    console.log("✅ Encrypted Password (first 50 chars):", encrypted.encryptedPassword.substring(0, 50));
    console.log("✅ Shared IV:", encrypted.iv);
    console.log("✅ Backend compatibility: Single IV for both fields ✓");

    // Decrypt recipient password
    console.log("Decrypting recipient email + password...");
    const decrypted = await crypto.decryptRecipientPassword(
      encrypted.encryptedEmail,
      encrypted.encryptedPassword,
      encrypted.iv,
      masterKeyData.masterKey
    );

    // Verify decryption
    if (decrypted.email === testEmail && decrypted.password === testPassword) {
      console.log("✅ Recipient password decryption successful");
      console.log("   Email:", decrypted.email);
      console.log("   Password:", decrypted.password);
    } else {
      throw new Error("Decrypted recipient password does not match original");
    }

    return true;
  } catch (error) {
    console.error("❌ Recipient password encryption/decryption failed:", error);
    return false;
  }
}

/**
 * Test 6: Full Workflow (User → Database → Recipient)
 */
async function testFullWorkflow() {
  console.log("\n=== Test 6: Full Workflow Integration ===");
  try {
    console.log("Simulating: User creates password database and shares with another user");

    // User A: Generate keypair
    console.log("\n[User A] Generating keypair...");
    const userAKeypair = await crypto.generateRSAKeypair();
    const userAPassword = "UserAPassword123!";

    // User A: Encrypt private key with password
    console.log("[User A] Encrypting private key...");
    const userAEncrypted = await crypto.encryptPrivateKeyWithPassword(
      userAKeypair.privateKey,
      userAPassword
    );

    // User A: Create password database (generate MasterKey)
    console.log("[User A] Creating password database (generating MasterKey)...");
    const masterKey = crypto.generateMasterKey();

    // User A: Encrypt MasterKey with own public key
    console.log("[User A] Encrypting MasterKey for self...");
    const userAMasterKeyEncrypted = await crypto.encryptMasterKeyWithPublicKey(
      masterKey,
      userAKeypair.publicKey
    );

    // User A: Add recipient password to database
    console.log("[User A] Adding recipient password to database...");
    const recipientData = await crypto.encryptRecipientPassword(
      "recipient@example.com",
      "RecipientPass123!",
      masterKey
    );
    console.log("[User A] Recipient data encrypted with single IV:", recipientData.iv);

    // User B: Generate keypair
    console.log("\n[User B] Generating keypair...");
    const userBKeypair = await crypto.generateRSAKeypair();

    // User A: Share database with User B (encrypt MasterKey with User B's public key)
    console.log("[User A] Sharing database with User B...");
    const userBMasterKeyEncrypted = await crypto.encryptMasterKeyWithPublicKey(
      masterKey,
      userBKeypair.publicKey
    );

    // User B: Decrypt MasterKey with own private key
    console.log("[User B] Decrypting MasterKey...");
    const userBMasterKey = await crypto.decryptMasterKeyWithPrivateKey(
      userBMasterKeyEncrypted,
      userBKeypair.privateKey
    );

    // Verify User B received correct MasterKey
    if (
      userBMasterKey.length === masterKey.length &&
      userBMasterKey.every((byte, i) => byte === masterKey[i])
    ) {
      console.log("✅ User B received correct MasterKey");
    } else {
      throw new Error("User B MasterKey mismatch");
    }

    // User B: Decrypt recipient password
    console.log("[User B] Decrypting recipient password...");
    const decryptedRecipient = await crypto.decryptRecipientPassword(
      recipientData.encryptedEmail,
      recipientData.encryptedPassword,
      recipientData.iv,
      userBMasterKey
    );

    console.log("✅ User B successfully decrypted recipient password:");
    console.log("   Email:", decryptedRecipient.email);
    console.log("   Password:", decryptedRecipient.password);

    console.log("\n✅ Full workflow integration test PASSED");
    return true;
  } catch (error) {
    console.error("❌ Full workflow integration test failed:", error);
    return false;
  }
}

/**
 * Run all crypto tests
 */
export async function runCryptoTests() {
  console.log("╔════════════════════════════════════════════╗");
  console.log("║   Crypto Module Test Suite                ║");
  console.log("╚════════════════════════════════════════════╝");

  let passCount = 0;
  let failCount = 0;

  // Test 1: KDF Detection
  if (await testKdfDetection()) passCount++;
  else failCount++;

  // Test 2: RSA Keypair
  const keypair = await testRsaKeypairGeneration();
  if (keypair) passCount++;
  else {
    failCount++;
    console.error("⚠️ Skipping remaining tests (RSA keypair generation failed)");
    return;
  }

  // Test 3: Private Key Encryption
  const privateKeyData = await testPrivateKeyEncryption(keypair);
  if (privateKeyData) passCount++;
  else failCount++;

  // Test 4: MasterKey Encryption
  const masterKeyData = await testMasterKeyEncryption(keypair);
  if (masterKeyData) passCount++;
  else failCount++;

  // Test 5: Recipient Password Encryption
  if (masterKeyData && (await testRecipientPasswordEncryption(masterKeyData))) passCount++;
  else failCount++;

  // Test 6: Full Workflow
  if (await testFullWorkflow()) passCount++;
  else failCount++;

  // Summary
  console.log("\n╔════════════════════════════════════════════╗");
  console.log("║   Test Summary                             ║");
  console.log("╚════════════════════════════════════════════╝");
  console.log(`✅ Passed: ${passCount}`);
  console.log(`❌ Failed: ${failCount}`);
  console.log(`Total: ${passCount + failCount}`);

  if (failCount === 0) {
    console.log("\n🎉 All crypto tests PASSED!");
  } else {
    console.warn(`\n⚠️ ${failCount} test(s) failed. Please review errors above.`);
  }
}

// Export for browser console usage
if (typeof window !== 'undefined') {
  window.runCryptoTests = runCryptoTests;
  console.log('[Crypto Tests] Test suite loaded! Run: runCryptoTests()');
}
